import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { copyToClipboard } from "@/lib/clipboard";
import { ROLE_LABELS } from "@/lib/agents";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import { UserPlus, Copy, Trash2, Mail, ShieldCheck, X, Loader2, Link as LinkIcon, Linkedin, Edit3 } from "lucide-react";

const ROLES = ["admin", "user", "readonly"];

export default function Team() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openInvite, setOpenInvite] = useState(false);
    const [lastInvite, setLastInvite] = useState(null);

    const refresh = async () => {
        const [m, i] = await Promise.all([api.get("/team/members"), api.get("/team/invites")]);
        setMembers(m.data);
        setInvites(i.data);
        setLoading(false);
    };
    useEffect(() => { refresh(); }, []);

    const isOwner = user?.role === "owner";
    const isAdmin = user?.role === "owner" || user?.role === "admin";

    const changeRole = async (uid, role) => {
        try { await api.patch(`/team/members/${uid}`, { role }); toast.success(t("team.role_updated")); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("team.failed")); }
    };
    const removeMember = async (uid) => {
        if (!confirm(t("team.deactivate_confirm"))) return;
        try { await api.delete(`/team/members/${uid}`); toast.success(t("team.member_deactivated")); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("team.failed")); }
    };
    const revokeInvite = async (id) => {
        try { await api.delete(`/team/invites/${id}`); toast.success(t("team.invite_revoked")); refresh(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("team.failed")); }
    };
    const updateIdentity = async (uid, linkedin_url, linkedin_handle) => {
        try {
            await api.patch(`/team/members/${uid}/identity`, { linkedin_url, linkedin_handle });
            toast.success(t("team.identity_updated"));
            refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || t("team.failed")); }
    };

    return (
        <PageHeader
            title={t("team.title")}
            subtitle={t("team.subtitle")}
            actions={isAdmin && (
                <button onClick={() => setOpenInvite(true)} data-testid="team-invite-open"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                    <UserPlus size={14} /> {t("team.invite_someone")}
                </button>
            )}
        >
            {loading ? <div className="text-[var(--jr-text-2)]">{t("common.loading")}</div> : (
                <div className="space-y-8">
                    {/* Members */}
                    <Section title={t("team.active_members")} count={members.length}>
                        <div className="space-y-2" data-testid="team-members-list">
                            {members.map((m) => (
                                <div key={m.id} data-testid={`member-${m.id}`}
                                    className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 flex items-center gap-4 flex-wrap">
                                    <div className="w-10 h-10 rounded-full bg-[var(--jr-brand)]/20 grid place-items-center text-[var(--jr-brand)] font-display font-medium">
                                        {(m.name || m.email)[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium truncate">{m.name}{m.id === user.id && <span className="ml-2 text-[10px] text-[var(--jr-text-3)] font-mono uppercase tracking-[0.16em]">({t("team.you")})</span>}</div>
                                        <div className="text-xs text-[var(--jr-text-2)] truncate">{m.email}</div>
                                        {m.linkedin_url && (
                                            <a href={m.linkedin_url} target="_blank" rel="noreferrer"
                                                data-testid={`member-linkedin-${m.id}`}
                                                className="inline-flex items-center gap-1 mt-1 text-[11px] text-[#0a66c2] hover:underline">
                                                <Linkedin size={10} /> {m.linkedin_handle || m.linkedin_url.replace(/^https?:\/\//, "")}
                                            </a>
                                        )}
                                    </div>
                                    <RoleBadge role={m.role} />
                                    <LinkedInEditor member={m} canEdit={isAdmin || m.id === user.id} onSave={updateIdentity} />
                                    {isOwner && m.id !== user.id && (
                                        <div className="flex items-center gap-1">
                                            <select value={m.role} data-testid={`role-select-${m.id}`}
                                                onChange={(e) => changeRole(m.id, e.target.value)}
                                                className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
                                                {["admin", "user", "readonly"].map((r) => (
                                                    <option key={r} value={r}>{ROLE_LABELS[r].label}</option>
                                                ))}
                                            </select>
                                            <button onClick={() => removeMember(m.id)} aria-label={t("team.remove_member")}
                                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-danger)] hover:bg-white/5">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Invites */}
                    <Section title={t("team.open_invites")} count={invites.length}>
                        {invites.length === 0 ? (
                            <div className="text-sm text-[var(--jr-text-3)] italic">{t("team.no_invites")}</div>
                        ) : (
                            <div className="space-y-2" data-testid="team-invites-list">
                                {invites.map((inv) => (
                                    <div key={inv.id} data-testid={`invite-${inv.id}`}
                                        className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 flex items-center gap-4">
                                        <Mail size={18} className="text-[var(--jr-amber)] shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-medium truncate">{inv.name} · <span className="text-[var(--jr-text-2)] font-normal">{inv.email}</span></div>
                                            <div className="text-xs text-[var(--jr-text-3)] font-mono uppercase tracking-[0.14em] mt-0.5">{t("team.magic_link_expires")} {new Date(inv.expires_at).toLocaleDateString()}</div>
                                        </div>
                                        <RoleBadge role={inv.role} />
                                        <button onClick={() => copyToClipboard(inv.accept_url, t("team.link_copied"))}
                                            data-testid={`invite-copy-${inv.id}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-[var(--jr-text-2)] hover:bg-white/5">
                                            <Copy size={12} /> {t("team.copy_link")}
                                        </button>
                                        {isAdmin && (
                                            <button onClick={() => revokeInvite(inv.id)} aria-label={t("team.revoke_invite")}
                                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-danger)]">
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>

                    {/* Role legend */}
                    <Section title={t("team.roles_title")}>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {["owner", "admin", "user", "readonly"].map((r) => (
                                <div key={r} className="rounded-lg border border-white/8 bg-[var(--jr-surface)] p-3 flex items-start gap-3">
                                    <ShieldCheck size={14} className="mt-0.5 shrink-0" style={{ color: ROLE_LABELS[r].color }} />
                                    <div>
                                        <div className="text-sm text-white font-medium">{ROLE_LABELS[r].label}</div>
                                        <div className="text-xs text-[var(--jr-text-2)]">{t(`team.role_desc.${r}`)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                </div>
            )}

            {openInvite && (
                <InviteModal onClose={() => setOpenInvite(false)}
                    onCreated={(inv) => { setLastInvite(inv); refresh(); }} />
            )}
            {lastInvite && <InviteSuccess invite={lastInvite} onClose={() => setLastInvite(null)} />}
        </PageHeader>
    );
}

/* ----- helpers ----- */
function Section({ title, count, children }) {
    return (
        <div>
            <div className="flex items-baseline gap-2 mb-3">
                <h3 className="font-display text-lg tracking-tight text-white">{title}</h3>
                {typeof count === "number" && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{count}</span>
                )}
            </div>
            {children}
        </div>
    );
}

function RoleBadge({ role }) {
    const r = ROLE_LABELS[role] || ROLE_LABELS.user;
    return (
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shrink-0"
            style={{ background: r.color + "22", color: r.color }}>
            {r.label}
        </span>
    );
}

function InviteModal({ onClose, onCreated }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({ email: "", name: "", role: "user" });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post("/team/invites", form);
            toast.success(t("team.invite_sent"));
            onCreated(data);
            onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || t("team.failed")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit} className="w-full max-w-md bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 jr-fadein max-h-[calc(100dvh-2rem)] overflow-y-auto"
                data-testid="invite-modal">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-2xl tracking-tight">{i18n.t("team.invite_someone")}</h3>
                    <button type="button" onClick={onClose} className="text-[var(--jr-text-2)] hover:text-white"><X size={20} /></button>
                </div>
                <div className="space-y-3">
                    <Field label={i18n.t("search.th_name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                        placeholder="Jane Doe" testid="invite-name" required />
                    <Field label="E-mail" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })}
                        placeholder="jane@bedrijf.io" testid="invite-email" required />
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Rol</label>
                        <select value={form.role} data-testid="invite-role"
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                            {ROLES.map((r) => (
                                <option key={r} value={r}>{ROLE_LABELS[r].label} — {ROLE_LABELS[r].desc}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={saving} data-testid="invite-submit"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {saving ? <><Loader2 className="animate-spin" size={14} /> {i18n.t("fbm.sending")}</> : i18n.t("team.send_invite")}
                    </button>
                </div>
            </form>
        </div>
    );
}

function InviteSuccess({ invite, onClose }) {
    const { t } = useTranslation();
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="w-full max-w-lg bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 jr-fadein max-h-[calc(100dvh-2rem)] overflow-y-auto"
                data-testid="invite-success">
                <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[var(--jr-brand)]/15 grid place-items-center"><LinkIcon size={14} className="text-[var(--jr-brand)]" /></div>
                    <h3 className="font-display text-xl tracking-tight">{t("team.magic_ready")}</h3>
                </div>
                <p className="text-sm text-[var(--jr-text-2)]">{t("team.magic_send_to")} <span className="text-white">{invite.email}</span>. {t("team.valid_14")}</p>
                <div className="mt-3 p-3 rounded-lg bg-[var(--jr-surface-2)] border border-white/10 font-mono text-xs text-[var(--jr-text-2)] break-all">{invite.accept_url}</div>
                <div className="mt-4 flex justify-end gap-2">
                    <button onClick={() => copyToClipboard(invite.accept_url, t("team.copied"))}
                        data-testid="invite-success-copy"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                        <Copy size={14} /> {t("team.copy_link")}
                    </button>
                    <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{t("common.close")}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testid }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}{required && " *"}</label>
            <input value={value} onChange={(e) => onChange(e.target.value)} type={type} required={required}
                placeholder={placeholder} data-testid={testid}
                className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
        </div>
    );
}

function LinkedInEditor({ member, canEdit, onSave }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [url, setUrl] = useState(member.linkedin_url || "");
    const [handle, setHandle] = useState(member.linkedin_handle || "");
    const [busy, setBusy] = useState(false);

    if (!canEdit) return null;

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await onSave(member.id, url.trim() || null, handle.trim() || null);
            setOpen(false);
        } finally { setBusy(false); }
    };

    return (
        <>
            <button onClick={() => setOpen(true)} data-testid={`edit-linkedin-${member.id}`}
                title={t("team.linkedin_edit_title")}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border transition-colors ${member.linkedin_url
                    ? "border-[#0a66c2]/35 bg-[#0a66c2]/10 text-[#0a66c2] hover:bg-[#0a66c2]/15"
                    : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                <Linkedin size={10} /> {member.linkedin_url ? "LinkedIn" : "+ LinkedIn"}
            </button>
            {open && (
                <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
                    onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
                    <form onSubmit={save} className="w-full max-w-md bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto"
                        data-testid={`linkedin-modal-${member.id}`}>
                        <h3 className="font-display text-lg tracking-tight">{t("team.linkedin_of")} {member.name}</h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">{t("team.linkedin_used_for", { name: member.name?.split(" ")[0] || "user" })}</p>
                        <div className="mt-4 space-y-3">
                            <Field label="LinkedIn URL" value={url} onChange={setUrl}
                                placeholder="https://linkedin.com/in/joost" testid={`linkedin-url-${member.id}`} type="url" />
                            <Field label={t("team.handle_optional")} value={handle} onChange={setHandle}
                                placeholder="@joost" testid={`linkedin-handle-${member.id}`} />
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" onClick={() => setOpen(false)} disabled={busy}
                                className="px-3 py-1.5 rounded-full border border-white/10 text-xs text-[var(--jr-text-2)] hover:bg-white/5">{t("common.cancel")}</button>
                            <button type="submit" disabled={busy} data-testid={`linkedin-save-${member.id}`}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium disabled:opacity-50">
                                {busy ? <Loader2 className="animate-spin" size={12} /> : <Edit3 size={12} />} {t("common.save")}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}
